/**
 * What to do about a role — the mockup's "Recommended actions", on live figures.
 *
 * Rendered inside an expanded role card, under the phase breakdown. The lights above say WHERE a
 * role is stuck; this says which lever to pull, which is the half a traffic light cannot carry.
 */
import type { RadarAction } from '../../../utils/radarActions';
import { RAMP_WEEKS } from '../../../utils/bottleneckRadar';
import { Dot } from './radarUi';

/**
 * ⚠️ The action dot reuses the TRAFFIC-LIGHT hues on purpose, and it is the same scale, not a
 * second one: a red action is the one belonging to a red signal. Giving advice its own palette
 * would put two colour systems on a card that already argues about this (see `radarStatus.ts`).
 */
export function ActionList({ actions }: { actions: RadarAction[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {actions.map((action) => (
        <li key={action.id} className="flex items-start gap-2.5">
          <span className="mt-[5px] shrink-0">
            <Dot status={action.severity} size="sm" />
          </span>
          <span className="text-[12.5px] leading-relaxed text-asphalt">{action.text}</span>
        </li>
      ))}
    </ul>
  );
}

export function RecommendedActions({ actions, ramp }: { actions: RadarAction[]; ramp: boolean }) {
  if (!actions.length) return null;
  return (
    <div className="rounded-lg border border-pearl bg-white p-3.5">
      <div className="mb-2.5 text-[10px] font-bold uppercase tracking-[1px] text-iron">
        What to do about it
      </div>
      {/*
        A ramping role's advice is shown but framed. Its reds have already been softened to amber,
        so the actions are real but early — and a recruiter told to "step up sourcing" in week two
        of a role is being sent after a problem the role has not had time to have yet.
      */}
      {ramp ? (
        <p className="mb-2.5 rounded-md border border-pearl bg-cotton px-2.5 py-2 text-[11px] leading-relaxed text-iron">
          This role is still ramping up ({RAMP_WEEKS} weeks or less). The signals are early, so
          treat these as things to watch rather than work to schedule.
        </p>
      ) : null}
      <ActionList actions={actions} />
    </div>
  );
}
